import React from "react";
import { Link } from "react-router-dom";
import PageHero from "../components/PageHero";
import { aboutValues, aboutTimeline, experience, brand, faqs } from "../mock";
import Certifications from "../components/Certifications";
import { ArrowRight, Download, Linkedin, Github, Mail } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const AboutPage = () => {
  return (
    <div>
      <PageHero
        eyebrow="About V4"
        title="Enterprise data engineering, delivered by certified specialists"
        subtitle="V4 Data And AI Solutions was founded to give enterprises what most consulting shops don’t — hands-on engineering execution backed by measurable outcomes."
        breadcrumb={[{ label: "Home", to: "/" }, { label: "About" }]}
      />

      <section className="py-20 bg-white dark:bg-[#08132C]">
        <div className="container-x grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5 reveal">
            <div className="rounded-2xl border border-slate-200 dark:border-white/10 p-8 bg-gradient-to-br from-[#F5F7FB] to-white dark:from-[#102248] dark:to-[#0B1B3B]">
              <div className="h-24 w-24 rounded-2xl bg-[#0B1B3B] text-white flex items-center justify-center text-3xl font-extrabold">VB</div>
              <h3 className="mt-6 text-xl font-bold text-[#0B1B3B] dark:text-white">Vishwajeet Bhangare</h3>
              <div className="text-sm text-slate-500 dark:text-slate-300">{brand.designation}</div>
              <p className="mt-4 text-sm text-slate-600 dark:text-slate-200 leading-relaxed">
                Strategic data engineering leader with 9+ years architecting enterprise-scale platforms across Azure, Snowflake, Databricks, Microsoft Fabric, Synapse and multi-cloud ecosystems. Founded V4 Data And AI Solutions to deliver end-to-end execution — not slide decks.
              </p>
              <div className="mt-6 flex items-center gap-3">
                <a href={brand.linkedin} target="_blank" rel="noreferrer" className="h-9 w-9 rounded-full border border-slate-200 dark:border-white/20 bg-white/90 dark:bg-white/5 text-[#0B1B3B] dark:text-white hover:border-[#1E56E3] hover:text-[#1E56E3] flex items-center justify-center transition-colors" data-testid="about-linkedin-button"><Linkedin size={16} /></a>
                <a href={brand.github} target="_blank" rel="noreferrer" className="h-9 w-9 rounded-full border border-slate-200 dark:border-white/20 bg-white/90 dark:bg-white/5 text-[#0B1B3B] dark:text-white hover:border-[#1E56E3] hover:text-[#1E56E3] flex items-center justify-center transition-colors" data-testid="about-github-button"><Github size={16} /></a>
                <a href={`mailto:${brand.email}`} className="h-9 w-9 rounded-full border border-slate-200 dark:border-white/20 bg-white/90 dark:bg-white/5 text-[#0B1B3B] dark:text-white hover:border-[#1E56E3] hover:text-[#1E56E3] flex items-center justify-center transition-colors" data-testid="about-email-button"><Mail size={16} /></a>
              </div>
              <a href={brand.resumeUrl} target="_blank" rel="noreferrer" className="btn-dark mt-6 inline-flex" data-testid="about-download-resume-button"><Download size={14} /> Download Resume</a>
            </div>
          </div>

          <div className="lg:col-span-7 reveal space-y-6">
            <span className="tag-chip">Our Story</span>
            <h2 className="section-title">From Fortune-500 engineering rooms to independent execution</h2>
            <p className="section-sub">
              After nine years engineering enterprise data platforms across Fortune-500 clients — spanning Snowflake performance programs, Microsoft Fabric consolidations, 10+ PB governance rollouts and Databricks lakehouse builds — V4 Data And AI Solutions was founded to bring that same rigor to businesses that need real outcomes, fast.
            </p>
            <p className="section-sub">
              We are not a body-shop. Every engagement is scoped around measurable outcomes: percentage query-performance gains, credit reductions, audit-compliance improvements, or platform-consolidation timelines. If we can’t define a measurable outcome upfront, we won’t take the engagement.
            </p>
            <div className="grid sm:grid-cols-2 gap-4 pt-4">
              {aboutValues.map((v) => (
                <div key={v.title} className="reveal rounded-xl border border-slate-200 p-5 hover-lift">
                  <div className="text-xs font-bold uppercase tracking-widest text-[#1E56E3]">Value</div>
                  <div className="mt-2 text-lg font-bold text-[#0B1B3B]">{v.title}</div>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#F5F7FB]">
        <div className="container-x">
          <div className="max-w-3xl reveal">
            <span className="tag-chip">Journey</span>
            <h2 className="section-title mt-4">A track record built engineering room by engineering room</h2>
          </div>
          <div className="mt-12 relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-slate-200 md:-translate-x-1/2" />
            <div className="space-y-8">
              {aboutTimeline.map((t, i) => (
                <div key={i} className={`reveal relative md:grid md:grid-cols-2 md:gap-10 ${i % 2 === 0 ? "" : "md:[&>*:first-child]:col-start-2"}`}>
                  <div className={`${i % 2 === 0 ? "md:text-right md:pr-10" : "md:pl-10 md:col-start-2"}`}>
                    <div className="pl-12 md:pl-0 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-[#0B1B3B]">
                      <span className="text-3xl font-extrabold text-[#1E56E3]">{t.year}</span>
                      <div className="mt-3 text-sm font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-300">{t.company}</div>
                      <div className="mt-1 text-lg font-bold text-[#0B1B3B] dark:text-white">{t.role}</div>
                      <p className="mt-3 text-[15px] text-slate-700 dark:text-slate-200 leading-relaxed">{t.event}</p>
                      <ul className={`mt-4 space-y-2 text-sm text-slate-600 dark:text-slate-300 ${i % 2 === 0 ? "md:ml-auto md:max-w-md" : "max-w-md"}`}>
                        {t.points.map((point) => (
                          <li key={point} className="flex items-start gap-2">
                            <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-[#1E56E3]" />
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <span className="absolute left-4 md:left-1/2 top-3 h-6 w-6 -translate-x-1/2 rounded-full bg-white border-4 border-[#1E56E3]" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Certifications />

      <section className="py-20 bg-[#F5F7FB]">
        <div className="container-x">
          <div className="max-w-3xl reveal">
            <span className="tag-chip">Experience</span>
            <h2 className="section-title mt-4">9+ years across enterprise data platforms</h2>
          </div>
          <div className="mt-10 grid md:grid-cols-2 gap-4">
            {experience.map((e, i) => (
              <div key={i} className="reveal rounded-xl bg-white border border-slate-200 p-6 hover-lift">
                <div className="text-xs font-semibold tracking-wider uppercase text-[#1E56E3]">{e.period}</div>
                <h3 className="mt-2 text-lg font-bold text-[#0B1B3B]">{e.role}</h3>
                <div className="text-sm text-slate-500">{e.company}</div>
                <p className="mt-3 text-[13px] text-slate-600 leading-relaxed">{e.highlight}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ merged in */}
      <section className="py-20 bg-white">
        <div className="container-x grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4 reveal">
            <span className="tag-chip">FAQs</span>
            <h2 className="section-title mt-4">Frequently asked questions</h2>
            <p className="section-sub mt-5">Common questions about our services, platforms and engagement models.</p>
            <Link to="/contact" className="btn-primary mt-6 inline-flex">Ask a different question <ArrowRight size={16} /></Link>
          </div>
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="w-full space-y-3">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="reveal border border-slate-200 rounded-xl px-5 bg-white hover:border-[#1E56E3]/30 transition-colors">
                  <AccordionTrigger className="text-left font-semibold text-[#0B1B3B] hover:no-underline py-5">
                    <span className="flex items-start gap-4">
                      <span className="text-[#1E56E3] font-mono text-sm mt-0.5">{String(i + 1).padStart(2, "0")}</span>
                      <span>{f.q}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-[14px] text-slate-600 leading-relaxed pb-5 pl-9">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
